import React, { useCallback, useEffect, useState } from 'react';

import alertKnockoutTokens from '@amzn/meridian-tokens/base/icon/alert-knockout';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Icon from '@amzn/meridian/icon';
import Pagination from '@amzn/meridian/pagination';
import Table, { TableCell, TableRow } from '@amzn/meridian/table';
import { type TableSortDirection } from '@amzn/meridian/table/table';
import Text from '@amzn/meridian/text';
import Tooltip from '@amzn/meridian/tooltip';
import moment from 'moment';
import UncontrolledExpandableRow from 'src/components/shared/uncontrolledExpandableRow/uncontrolledExpandableRow';
import { TRANSFER_PENDING } from 'src/config/AccommodationStatusNames';
import * as AccommodationStatusNames from 'src/config/AccommodationStatusNames';
import JmrService from 'src/services/jmr/jmrService';
import { type PlacementDetailsRevision } from 'src/store/placementDetailsPageSlice/types';
import { useAppSelector } from 'src/store/store';
import { createFileUrl, downloadFile } from 'src/utils/fileUtils';
import { sortAndPaginateTableData } from 'src/utils/tableUtils';

import './revisionHistory.scss';

const RevisionHistory = () => {
  const { placementDetails } = useAppSelector((state) => state.placementDetailsPage);
  const [sortColumn, setSortColumn] = useState<keyof PlacementDetailsRevision>('revisionDate');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [sortDirection, setSortDirection] = useState<TableSortDirection>('descending');
  const onSort = useCallback(
    ({
      sortColumn,
      sortDirection,
    }: {
      sortColumn: keyof PlacementDetailsRevision;
      sortDirection: TableSortDirection;
    }) => {
      setSortDirection(sortDirection);
      setSortColumn(sortColumn);
    },
    [],
  );
  const [sortedTableData, setSortedTableData] = useState(placementDetails.revisions);

  useEffect(() => {
    // filter, sorting, and pagination
    if (placementDetails?.revisions) {
      const { paginatedData, totalPages } = sortAndPaginateTableData(
        [...placementDetails.revisions],
        sortColumn,
        sortDirection,
        currentPage,
        20,
      );
      setSortedTableData(paginatedData);
      setTotalPages(totalPages);
    }
  }, [sortColumn, sortDirection, placementDetails.revisions, currentPage]);

  const downloadJmr = useCallback(
    (filename: string) => async () => {
      const { data } = await JmrService.downloadJMR(filename);
      if (data) {
        downloadFile(createFileUrl(data, 'application/pdf'), 'jmr-report.pdf');
      }
    },
    [],
  );

  // Format a revision timestamp as "M/D/YYYY (hh:mm:ss) UTC"
  const formatRevisionDateTime = (revisionDate: number) => {
    return moment.utc(revisionDate).format('M/D/YYYY (HH:mm:ss) [UTC]');
  };

  return (
    <Column alignmentHorizontal="center" spacing={'400'}>
      <Table
        headerRows={1}
        rowComponents={[UncontrolledExpandableRow, TableRow]}
        showStripes={true}
        onSort={onSort}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        showDividers={true}
      >
        <TableRow>
          <TableCell sortColumn="revisionDate">Revision Date</TableCell>
          <TableCell sortColumn="status">Status Update</TableCell>
          <TableCell>Download JMR Link</TableCell>
          <TableCell sortColumn="revisedBy">Revised By</TableCell>
        </TableRow>

        {sortedTableData?.map((revision, index) => (
          <TableRow key={index}>
            <TableCell data-cy={`placement-details__revision__date-${index}`}>
              {revision.revisionDate && formatRevisionDateTime(revision.revisionDate)}
            </TableCell>
            <TableCell data-cy={`placement-details__revision__status-${index}`}>
              {revision.status.status}
              {revision.status.status === TRANSFER_PENDING && revision.previousFc !== null && (
                <span> (from {revision.previousFc})</span>
              )}
              {(revision.status.status === 'Associate Decision Pending' || revision.status.status === 'Denied TWP') &&
                revision.decisionNotes !== null && (
                  <Text>
                    <strong>Decision Notes: </strong>
                    {revision.decisionNotes}
                  </Text>
                )}
              {revision.status.status === AccommodationStatusNames.DLS_REFERRAL && !!revision.dlsReferralReason && (
                <Text>
                  <strong>Reason for Referral: </strong>
                  {revision.dlsReferralReason.description}
                </Text>
              )}
            </TableCell>
            <TableCell>
              {revision.jmrFileName && (
                <>
                  <Button
                    data-cy={`placement-details__revision__download-${index}`}
                    type="link"
                    onClick={downloadJmr(revision.jmrFileName)}
                  >
                    {(revision.revisionDate && 'Download JMR') || ''}
                  </Button>
                  {revision.austinUploadFailed && (
                    <Tooltip
                      position="top"
                      title="JMR upload to AUSTIN in progress. Please wait a few minutes and refresh. If unsuccessful, return to AUSTIN for manual upload using FirstName_LastName_DocumentName_Date(YYYY-MM-DD). Example: John_Doe_JMR_2020-02-28."
                    >
                      <Button className="warning" type="link">
                        <Icon tokens={alertKnockoutTokens} />
                      </Button>
                    </Tooltip>
                  )}
                </>
              )}
            </TableCell>
            <TableCell data-cy={`placement-details__revision__revisedBy-${index}`}>{revision.revisedBy}</TableCell>
          </TableRow>
        ))}
      </Table>
      <Pagination
        numberOfPages={totalPages}
        onChange={setCurrentPage}
        currentPage={currentPage}
        showSkipArrows={true}
      />
    </Column>
  );
};

export default RevisionHistory;
